import { ScrollArea } from '@/components/ui/scroll-area';
import useStore from '@/lib/store';
import { cn } from '@/lib/utils';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  NavLink,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { AgroEventsResponse } from '../models/IAgroTechnicalEvents';
import { Button } from '@/components/ui/button.tsx';
import { BackIcon } from '@/common/icons/BackIcon.tsx';

interface AgroTechnicalEventsSidebarProps {
  className?: string;
  items?: AgroEventsResponse[];
  isLoading?: boolean;
}

export const AgroTechnicalEventsSidebar: FC<
  AgroTechnicalEventsSidebarProps
> = ({ items = [], isLoading }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { id, status } = useParams();
  const safeItems = Array.isArray(items) ? items : [];
  const { plantsSelect } = useStore();

  const navigate = useNavigate();

  return (
    <div
      className={`flex flex-col w-[400px] min-w-[400px] bg-white h-screen overflow-hidden border-r`}
    >
      <ScrollArea className={cn('flex-1')}>
        <div className="relative">
          <div className="sticky top-0 left-0 bg-white z-[2] h-[73px] flex items-center py-2.5 px-6 border-b">
            <Button
              onClick={() => navigate(-1)}
              variant="outline"
              className="!py-0 px-2 h-[32px] mr-4"
            >
              <BackIcon />
            </Button>
            <p className="text-lg leading-7 text-midnight-default font-medium">
              {t('all.agro.technical.events')}
            </p>
          </div>
          {safeItems.length === 0 && !isLoading ? (
            <div className="text-center text-black text-xl py-4">
              {t('no.data')}
            </div>
          ) : (
            safeItems.map((item, i) => (
              <NavLink
                key={i}
                to={{
                  pathname: `${status || 'LAND'}/${item?.project_id}`,
                  search: (() => {
                    const params = new URLSearchParams(searchParams);
                    if (!params.has('month')) {
                      params.set('month', 'JANUARY');
                    }
                    return params.toString();
                  })(),
                }}
                className={cn(
                  'py-3 px-6 border-b bg-white cursor-pointer  transition-all grid grid-cols-12 gap-[10px] relative  duration-300',
                  id === item?.project_id && 'bg-green-default/10',
                )}
              >
                <span
                  className={cn(
                    id === item?.project_id
                      ? 'bg-green-default  opacity-100'
                      : 'opacity-0',
                    'duration-300 absolute left-0 inset-y-0 h-full w-1',
                  )}
                />
                <div className="flex flex-col items-start col-span-6">
                  <p className="text-[11px] font-medium text-gray-graphite">
                    {t('farmer')}
                  </p>
                  <p className="text-sm text-midnight-default font-semibold">
                    {item?.apprentice_name}
                  </p>
                </div>
                <div className="flex flex-col items-start col-span-6">
                  <p className="text-[11px] font-medium text-gray-graphite">
                    {t('farmer.stir')}
                  </p>
                  <p className="text-sm text-midnight-default">
                    {item?.tax_number}
                  </p>
                </div>
                <div className="flex flex-col items-start col-span-6">
                  <p className="text-[11px] font-medium text-gray-graphite">
                    {t('lands')}
                  </p>
                  <p className="text-sm text-midnight-default">
                    {item?.region_name}
                  </p>
                </div>
                <div className="flex flex-col items-start col-span-6">
                  <p className="text-[11px] font-medium text-gray-graphite">
                    {t('lot.number')}
                  </p>
                  <p className="text-sm text-dark-blue w-[200px] font-semibold line-clamp-1">
                    {item?.regnum}
                  </p>
                </div>

                <div className="flex flex-col items-start col-span-6">
                  <p className="text-[11px] font-medium text-gray-graphite">
                    {t('area')}
                  </p>
                  <p className="text-sm text-midnight-default">
                    {item?.area} ga
                  </p>
                </div>
                <div className="flex flex-col items-start col-span-6">
                  <p className="text-[11px] font-medium text-gray-graphite">
                    {t('plant.type')}
                  </p>
                  <p className="text-sm text-midnight-default">
                    {plantsSelect?.find((o) => o?.value === item?.plant_id)
                      ?.label ?? '-'}
                  </p>
                </div>
              </NavLink>
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
